import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  ApiError,
  acceptFamilyInvitation,
  declineFamilyInvitation,
  viewFamilyInvitation,
} from '../lib/api.js';
import {
  FAMILY_PATH,
  agreementsLine,
  invitationTokenOf,
  nameOf,
  refusalText,
  type InvitationPreview,
} from '../lib/family.js';
import { formatDay } from '../lib/subscriptions.js';
import { Note, Problem } from '../components/form.js';
import { GuardianCanSee, st } from './Family.js';

/**
 * The invitation page (RidgeWill/account#117 screen 4), at
 * `/family/join?invitation=<token>`, from the one email the invitee gets.
 *
 * Behind sign-in like every page here, including account creation for an
 * address with no account: the hosted login brings them back to this URL.
 *
 * ── EXACTLY WHAT JOINING MEANS, BEFORE ANYONE DECIDES ────────────────
 *
 * A dependent invitation shows what the guardian can see and do, in the
 * privacy policy's words, and what happens to agreements by age (auth#625:
 * anyone can join; guardians accept only for under 18). "Decline" ends it, and
 * guardian" ends it: the guardian is told in the portal, and nobody is emailed.
 *
 * ── PASSES THE AGREEMENT GATE ────────────────────────────────────────
 *
 * Someone under 18 owes a guardian's half of every agreement until a guardian
 * accepts it, so this page has to open for them before anything else does.
 * App.tsx lets it through.
 */
/**
 * Refusals after which the invitation is over (Jon, 2026-10-14): auth ended it
 * (`INVITED_AS_ADULT` marks it `refused_minor`), or it was already used,
 * withdrawn or expired. The page then stops offering Join and Decline and the
 * "stays open until" line, which were no longer true.
 */
const INVITATION_ENDED = new Set(['INVITED_AS_ADULT', 'INVITATION_NOT_FOUND', 'INVITATION_EXPIRED']);

export function FamilyJoin() {
  const token = invitationTokenOf(window.location.search);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'join' | 'decline' | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** Set when a refusal has ended the invitation: nothing is left to join or decline. */
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!token) return;
    viewFamilyInvitation(token).then(setPreview, (e: unknown) =>
      setError(
        refusalText(e instanceof ApiError ? e.code : undefined, e instanceof Error ? e.message : 'This invitation could not be opened.'),
      ),
    );
  }, [token]);

  if (!token) {
    return (
      <section style={{ maxWidth: 640 }}>
        <h2 style={st.h2}>Family Group invitation</h2>
        <Note>This link is missing its invitation. Open the link from the email again.</Note>
      </section>
    );
  }

  if (done) {
    return (
      <section style={{ maxWidth: 640 }}>
        <p role="status" style={st.p}>
          {done}
        </p>
        <a href={FAMILY_PATH} style={{ color: C.brand }}>
          Go to your Family Group
        </a>
      </section>
    );
  }

  if (error && !preview) {
    return (
      <section style={{ maxWidth: 640 }}>
        <h2 style={st.h2}>Family Group invitation</h2>
        <Problem>{error}</Problem>
      </section>
    );
  }
  if (!preview) return <Note>Loading…</Note>;

  const inviter = nameOf(preview.inviter);
  const dependent = preview.kind === 'dependent';

  async function act(kind: 'join' | 'decline') {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'join') {
        await acceptFamilyInvitation(token!);
        setDone(`You joined ${inviter}'s Family Group.`);
      } else {
        await declineFamilyInvitation(token!);
        setDone(`You declined. ${inviter} has been told, and nothing else will be sent.`);
      }
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      setError(refusalText(code, e instanceof Error ? e.message : 'That did not go through.'));
      if (code && INVITATION_ENDED.has(code)) setClosed(true);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section style={{ maxWidth: 640 }}>
      <h2 style={st.h2}>Join {inviter}&rsquo;s Family Group?</h2>
      <p style={st.p}>
        {dependent
          ? `${inviter} invited you to join their family group. A family group brings together the people in a family. Here is exactly what ${inviter} would see and do as a guardian.`
          : `${inviter} invited you to help manage their Family Group as another parent or guardian. You can accept agreements for its dependents and see what their guardians can see.`}
      </p>

      {dependent && <GuardianCanSee guardianName={inviter} />}

      {dependent && (
        <div style={{ ...st.callout, marginTop: '1.25rem' }}>
          <p style={{ margin: 0 }}>{agreementsLine(inviter)}</p>
        </div>
      )}

      {!closed && <Note>This invitation stays open until {formatDay(preview.expiresAt)}.</Note>}
      {error && <Problem>{error}</Problem>}

      {!closed && (
        <>
          <div style={st.actions}>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void act('join')}
              style={st.primary(busy !== null)}
            >
              {busy === 'join' ? 'Joining…' : 'Join the Family Group'}
            </button>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button type="button" disabled={busy !== null} onClick={() => void act('decline')} style={st.quiet}>
              Decline
            </button>
          </div>
        </>
      )}
    </section>
  );
}
